<template>
  <div class="reports">
    <v-container fluid class="px-6 py-4">
      <!-- Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="header-text">
            <h1 class="page-title">
              <v-icon large class="title-icon mr-3">mdi-chart-bar</v-icon>
              CRM Hygiene Reports
            </h1>
            <p class="page-subtitle">
              Monitor and maintain data quality across your CRM system
            </p>
          </div>
          <v-btn
            large
            color="primary"
            class="generate-btn"
            @click="generateReport"
            :loading="isGenerating"
            elevation="2"
          >
            <v-icon left>mdi-file-chart</v-icon>
            Generate New Report
          </v-btn>
        </div>
      </div>

      <!-- Quick Stats -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="2">
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper blue--bg">
                <v-icon size="24" color="white">mdi-file-multiple</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ reports.length }}</div>
                <div class="stats-label">Total Reports</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="2">
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper green--bg">
                <v-icon size="24" color="white">mdi-check-circle</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ averageQualityScore }}%</div>
                <div class="stats-label">Avg Quality Score</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="2">
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper orange--bg">
                <v-icon size="24" color="white">mdi-wrench</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ totalIssuesResolved }}</div>
                <div class="stats-label">Issues Resolved</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="2">
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper purple--bg">
                <v-icon size="24" color="white">mdi-clock</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ totalTimeSaved }}h</div>
                <div class="stats-label">Time Saved</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Filters -->
      <v-card class="filters-card mb-6" elevation="2">
        <v-card-text class="pa-4">
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.report_type"
                :items="reportTypeOptions"
                label="Report Type"
                clearable
                dense
                outlined
                @change="fetchReports"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.date_range"
                :items="dateRangeOptions"
                label="Date Range"
                dense
                outlined
                @change="fetchReports"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.crm_platform"
                :items="crmPlatformOptions"
                label="CRM Platform"
                clearable
                dense
                outlined
                @change="fetchReports"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filters.search"
                label="Search"
                clearable
                dense
                outlined
                append-icon="mdi-magnify"
                @input="debouncedSearch"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Reports Table -->
      <v-card elevation="4">
        <v-card-title class="pa-4">
          <h3>Hygiene Reports</h3>
        </v-card-title>
        
        <v-data-table
          :headers="headers"
          :items="reports"
          :loading="isLoading"
          class="elevation-0"
        >
          <template v-slot:[`item.report_type`]="{ item }">
            <v-chip
              color="blue"
              text-color="white"
              small
              label
            >
              {{ formatReportType(item.report_type) }}
            </v-chip>
          </template>

          <template v-slot:[`item.overall_quality_score`]="{ item }">
            <div class="quality-score">
              <v-progress-linear
                :value="item.overall_quality_score || 0"
                :color="getQualityColor(item.overall_quality_score)"
                height="6"
                rounded
                class="mb-1"
              ></v-progress-linear>
              <span class="score-text">{{ Math.round(item.overall_quality_score || 0) }}%</span>
            </div>
          </template>

          <template v-slot:[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              icon
              small
              color="primary"
              @click="viewReport(item)"
            >
              <v-icon>mdi-eye</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              color="success"
              @click="downloadReport(item)"
            >
              <v-icon>mdi-download</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>

      <!-- View Report Dialog -->
      <v-dialog v-model="showViewDialog" max-width="900px">
        <v-card v-if="selectedReport">
          <v-card-title class="headline">
            Report: {{ selectedReport.report_name }}
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <strong>Report Type:</strong> {{ formatReportType(selectedReport.report_type) }}
              </v-col>
              <v-col cols="12" md="6">
                <strong>Quality Score:</strong> {{ Math.round(selectedReport.overall_quality_score || 0) }}%
              </v-col>
              <v-col cols="12" md="6">
                <strong>Records Analyzed:</strong> {{ selectedReport.total_records_analyzed || 0 }}
              </v-col>
              <v-col cols="12" md="6">
                <strong>Issues Found:</strong> {{ selectedReport.total_issues_found || 0 }}
              </v-col>
            </v-row>
            
            <v-divider class="my-4"></v-divider>
            
            <div v-if="selectedReport.report_data" class="mb-4">
              <h3 class="mb-2">Report Data</h3>
              <v-card outlined>
                <v-card-text>
                  <div v-if="selectedReport.report_data.quality_metrics" class="mb-4">
                    <h4>Quality Metrics</h4>
                    <v-row>
                      <v-col cols="4">
                        <div class="metric-item">
                          <div class="metric-value">{{ selectedReport.report_data.quality_metrics.completeness || 0 }}%</div>
                          <div class="metric-label">Completeness</div>
                        </div>
                      </v-col>
                      <v-col cols="4">
                        <div class="metric-item">
                          <div class="metric-value">{{ selectedReport.report_data.quality_metrics.accuracy || 0 }}%</div>
                          <div class="metric-label">Accuracy</div>
                        </div>
                      </v-col>
                      <v-col cols="4">
                        <div class="metric-item">
                          <div class="metric-value">{{ selectedReport.report_data.quality_metrics.consistency || 0 }}%</div>
                          <div class="metric-label">Consistency</div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                  
                  <div v-if="selectedReport.report_data.top_issues" class="mb-4">
                    <h4>Top Issues</h4>
                    <v-list dense>
                      <v-list-item v-for="(issue, index) in selectedReport.report_data.top_issues" :key="index">
                        <v-list-item-icon>
                          <v-icon color="warning">mdi-alert</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>{{ issue }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </div>
                </v-card-text>
              </v-card>
            </div>
            
            <div v-if="selectedReport.recommendations" class="mb-4">
              <h3 class="mb-2">Recommendations</h3>
              <v-card outlined>
                <v-card-text>
                  <div class="recommendation-text">
                    {{ selectedReport.recommendations }}
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="closeViewDialog">Close</v-btn>
            <v-btn color="success" @click="downloadReport(selectedReport)">
              <v-icon left>mdi-download</v-icon>
              Download
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import axios from "@/axios";
import { debounce } from "lodash";

export default {
  name: "CRMGenieReports",
  data() {
    return {
      isLoading: false,
      isGenerating: false,
      showViewDialog: false,
      selectedReport: null,
      reports: [],
      filters: {
        report_type: null,
        date_range: '30',
        crm_platform: null,
        search: null,
      },
      headers: [
        { text: 'Report Name', value: 'report_name', sortable: true },
        { text: 'Type', value: 'report_type', sortable: false },
        { text: 'Records Analyzed', value: 'total_records_analyzed', sortable: true },
        { text: 'Issues Found', value: 'total_issues_found', sortable: true },
        { text: 'Quality Score', value: 'overall_quality_score', sortable: true },
        { text: 'Generated', value: 'created_at', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
      ],
      reportTypeOptions: [
        { text: 'Data Quality', value: 'data_quality' },
        { text: 'Duplicate Analysis', value: 'duplicate_analysis' },
        { text: 'Missing Fields', value: 'missing_fields' },
        { text: 'Enrichment Summary', value: 'enrichment_summary' },
        { text: 'Weekly Hygiene', value: 'weekly_hygiene' },
      ],
      dateRangeOptions: [
        { text: 'Last 7 days', value: '7' },
        { text: 'Last 30 days', value: '30' },
        { text: 'Last 90 days', value: '90' },
        { text: 'Last year', value: '365' },
      ],
      crmPlatformOptions: [
        { text: 'Salesforce', value: 'salesforce' },
        { text: 'HubSpot', value: 'hubspot' },
        { text: 'Pipedrive', value: 'pipedrive' },
        { text: 'Zoho', value: 'zoho' },
        { text: 'Custom', value: 'custom' },
      ],
    };
  },
  computed: {
    debouncedSearch() {
      return debounce(this.fetchReports, 300);
    },
    averageQualityScore() {
      if (this.reports.length === 0) return 0;
      const sum = this.reports.reduce((acc, report) => acc + (report.overall_quality_score || 0), 0);
      return Math.round(sum / this.reports.length);
    },
    totalIssuesResolved() {
      return this.reports.reduce((acc, report) => acc + (report.issues_resolved || 0), 0);
    },
    totalTimeSaved() {
      return this.reports.reduce((acc, report) => acc + (report.estimated_time_saved_hours || 0), 0);
    },
  },
  async mounted() {
    await this.fetchReports();
  },
  methods: {
    async fetchReports() {
      try {
        this.isLoading = true;
        const params = { ...this.filters };
        
        // Remove empty filters
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key];
          }
        });

        const response = await axios.get('crm-hygiene-reports', { params });
        
        if (response.data.success) {
          this.reports = response.data.data;
        }
      } catch (error) {
        console.error('Error fetching reports:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to load hygiene reports. Please try again.']
        });
      } finally {
        this.isLoading = false;
      }
    },

    async generateReport() {
      try {
        this.isGenerating = true;
        const response = await axios.post('crm-hygiene-reports/generate', {
          report_name: `Data Quality Report - ${new Date().toLocaleDateString()}`,
          report_type: 'data_quality',
        });
        
        if (response.data.success) {
          this.$store.dispatch('errors/setSuccess', 'Report generation started successfully');
          setTimeout(() => {
            this.fetchReports();
          }, 2000);
        }
      } catch (error) {
        console.error('Error generating report:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to generate report. Please try again.']
        });
      } finally {
        this.isGenerating = false;
      }
    },

    async downloadReport(report) {
      try {
        this.isLoading = true;
        const response = await axios.get(`crm-hygiene-reports/${report.id}/download`, {
          responseType: 'blob'
        });
        
        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `hygiene-report-${report.id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        this.$store.dispatch('errors/setSuccess', 'Report downloaded successfully');
      } catch (error) {
        console.error('Error downloading report:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to download report. Please try again.']
        });
      } finally {
        this.isLoading = false;
      }
    },

    viewReport(report) {
      this.selectedReport = report;
      this.showViewDialog = true;
    },

    closeViewDialog() {
      this.showViewDialog = false;
      this.selectedReport = null;
    },

    getQualityColor(score) {
      if (score >= 80) return 'green';
      if (score >= 60) return 'orange';
      return 'red';
    },

    formatReportType(type) {
      const types = {
        'data_quality': 'Data Quality',
        'duplicate_analysis': 'Duplicate Analysis',
        'missing_fields': 'Missing Fields',
        'enrichment_summary': 'Enrichment Summary',
        'weekly_hygiene': 'Weekly Hygiene'
      };
      return types[type] || type;
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
  },
};
</script>

<style scoped>
.reports {
  min-height: calc(100vh - 64px);
}

.page-header {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.title-icon {
  color: #667eea;
}

.page-subtitle {
  font-size: 16px;
  color: #64748b;
  margin: 8px 0 0 0;
  font-weight: 400;
}

.generate-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  height: 48px !important;
}

.stats-card {
  border-radius: 12px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  transition: all 0.3s ease;
}

.stats-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(102, 126, 234, 0.15) !important;
}

.stats-content {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px !important;
}

.stats-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.orange--bg { background: linear-gradient(135deg, #ff9500 0%, #ff6b00 100%); }
.green--bg { background: linear-gradient(135deg, #51cf66 0%, #40c057 100%); }
.blue--bg { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
.purple--bg { background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%); }

.stats-text {
  flex: 1;
}

.stats-value {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1;
  margin-bottom: 4px;
}

.stats-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filters-card {
  border-radius: 12px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.quality-score {
  min-width: 100px;
}

.score-text {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.metric-item {
  text-align: center;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 8px;
}

.metric-value {
  font-size: 20px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 4px;
}

.metric-label {
  font-size: 12px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.recommendation-text {
  padding: 16px;
  background: #e3f2fd;
  border-radius: 8px;
  border-left: 4px solid #2196f3;
  font-style: italic;
}

@media (max-width: 960px) {
  .header-content {
    flex-direction: column;
    text-align: center;
  }
  
  .page-title {
    font-size: 28px;
    justify-content: center;
  }
  
  .page-header {
    padding: 24px 20px;
  }
}

@media (max-width: 600px) {
  .page-header {
    padding: 20px 16px;
  }
  
  .page-title {
    font-size: 24px;
    flex-direction: column;
    gap: 8px;
  }
  
  .generate-btn {
    width: 100%;
  }
}
</style>